import { useRef, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import {
  useQueryState,
  useGetList,
  usePrevious,
  IconLoader,
  ButtonSecondary,
  ButtonSize,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { volumesSchema } from '../variables/volumes';
import VolumesItem from '../../../components/volumes-risk/volumes-item';
import {
  ClientRange,
  RiskScoreClientTypeEnum,
  SettingClientRangeType,
} from '../../types';
import VolumesListItem from './list-item';
import { setActiveState as setCorporateActiveState } from '../../corporate-clients-risk/store/slices/volumes-list';
import { setActiveState as setIndividualActiveState } from '../../indiviual-clients-risk/store/slices/volumes-list';
import { VolumesListTypes } from './types';

import cn from '../partial.module.css';

const VolumesList = ({
  getList,
  isLoading,
  isFetching,
  values,
  type = RiskScoreClientTypeEnum.INDIVIDUAL,
}: VolumesListTypes) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const dispatch = useAppDispatch();

  const {
    amlDictionaries: { activePermissions, riskScoreRanges, baseCurrency },
    corporateClientsRisk: {
      volumes: {
        list: { activeState: corporateActiveState },
      },
    },
    individualClientsRisk: {
      volumes: {
        list: { activeState: individualActiveState },
      },
    },
  } = useAppSelector((state) => state);

  const { sort, onSortChange, filters, onFilterChange } =
    useQueryState(volumesSchema);

  const { getListAction, errorList } = useGetList<ClientRange>({
    getList,
    onFilterChange,
    onSortChange,
    schema: volumesSchema,
  });

  const prevFilters = usePrevious(filters);

  useEffect(() => {
    if (prevFilters && !Object.keys(prevFilters).length && filters && !values) {
      getListAction({
        filters,
        sort,
      });
    }
  }, [filters, prevFilters, sort, values]);

  let content;

  if (isLoading) {
    content = (
      <div className={cn.volumesLoader}>
        <IconLoader />
      </div>
    );
  } else {
    let activeButtonText = '';

    if (type === RiskScoreClientTypeEnum.INDIVIDUAL) {
      if (individualActiveState) {
        activeButtonText = 'Inactive';
      } else {
        activeButtonText = 'Active';
      }
    } else if (type === RiskScoreClientTypeEnum.CORPORATE) {
      if (corporateActiveState) {
        activeButtonText = 'Inactive';
      } else {
        activeButtonText = 'Active';
      }
    }

    content = (
      <>
        <ButtonSecondary
          className={`button-white ${cn.activeStateButton}`}
          size={ButtonSize.SMALL}
          onClick={() => {
            if (type === RiskScoreClientTypeEnum.INDIVIDUAL) {
              dispatch(setIndividualActiveState(!individualActiveState));
            } else if (type === RiskScoreClientTypeEnum.CORPORATE) {
              dispatch(setCorporateActiveState(!corporateActiveState));
            }

            getList(
              `?take=99&active=${
                type === RiskScoreClientTypeEnum.INDIVIDUAL
                  ? !individualActiveState
                  : !corporateActiveState
              }`,
            );
          }}
          text={activeButtonText}
        />
        <VolumesItem title="Turnover">
          <VolumesListItem
            list={values?.filter(
              (item) => item.type === SettingClientRangeType.MONTHLY_TURNOVER,
            )}
            error={errorList}
            sort={sort}
            filters={filters}
            riskScoreRanges={riskScoreRanges}
            activePermissions={activePermissions}
            isFetching={isFetching}
            type={SettingClientRangeType.MONTHLY_TURNOVER}
            baseCurrency={baseCurrency}
            clientType={type}
          />
        </VolumesItem>
        <VolumesItem title="N. of incoming transactions">
          <VolumesListItem
            list={values?.filter(
              (item) =>
                item.type ===
                SettingClientRangeType.MONTHLY_NUMBER_OF_INCOMING_TRANSACTIONS,
            )}
            error={errorList}
            sort={sort}
            filters={filters}
            riskScoreRanges={riskScoreRanges}
            activePermissions={activePermissions}
            isFetching={isFetching}
            type={
              SettingClientRangeType.MONTHLY_NUMBER_OF_INCOMING_TRANSACTIONS
            }
            clientType={type}
          />
        </VolumesItem>
        <VolumesItem title="N. of outgoing transactions">
          <VolumesListItem
            list={values?.filter(
              (item) =>
                item.type ===
                SettingClientRangeType.MONTHLY_NUMBER_OF_OUTGOING_TRANSACTIONS,
            )}
            sort={sort}
            error={errorList}
            filters={filters}
            riskScoreRanges={riskScoreRanges}
            activePermissions={activePermissions}
            isFetching={isFetching}
            type={
              SettingClientRangeType.MONTHLY_NUMBER_OF_OUTGOING_TRANSACTIONS
            }
            clientType={type}
          />
        </VolumesItem>
        <VolumesItem title="Value of incoming transactions">
          <VolumesListItem
            list={values?.filter(
              (item) =>
                item.type ===
                SettingClientRangeType.MONTHLY_VALUE_OF_INCOMING_TRANSACTIONS,
            )}
            sort={sort}
            filters={filters}
            riskScoreRanges={riskScoreRanges}
            error={errorList}
            activePermissions={activePermissions}
            isFetching={isFetching}
            type={SettingClientRangeType.MONTHLY_VALUE_OF_INCOMING_TRANSACTIONS}
            baseCurrency={baseCurrency}
            clientType={type}
          />
        </VolumesItem>
        <VolumesItem title="Value of outgoing transactions">
          <VolumesListItem
            list={values?.filter(
              (item) =>
                item.type ===
                SettingClientRangeType.MONTHLY_VALUE_OF_OUTGOING_TRANSACTIONS,
            )}
            sort={sort}
            filters={filters}
            riskScoreRanges={riskScoreRanges}
            activePermissions={activePermissions}
            error={errorList}
            isFetching={isFetching}
            type={SettingClientRangeType.MONTHLY_VALUE_OF_OUTGOING_TRANSACTIONS}
            baseCurrency={baseCurrency}
            clientType={type}
          />
        </VolumesItem>
        <VolumesItem title="Max. value of incoming payment">
          <VolumesListItem
            list={values?.filter(
              (item) =>
                item.type ===
                SettingClientRangeType.MONTHLY_MAX_VALUE_OF_INCOMING_PAYMENT,
            )}
            sort={sort}
            filters={filters}
            riskScoreRanges={riskScoreRanges}
            activePermissions={activePermissions}
            error={errorList}
            isFetching={isFetching}
            type={SettingClientRangeType.MONTHLY_MAX_VALUE_OF_INCOMING_PAYMENT}
            baseCurrency={baseCurrency}
            clientType={type}
          />
        </VolumesItem>
        <VolumesItem title="Max. value of outgoing payment">
          <VolumesListItem
            list={values?.filter(
              (item) =>
                item.type ===
                SettingClientRangeType.MONTHLY_MAX_VALUE_OF_OUTGOING_PAYMENT,
            )}
            sort={sort}
            filters={filters}
            riskScoreRanges={riskScoreRanges}
            activePermissions={activePermissions}
            error={errorList}
            isFetching={isFetching}
            type={SettingClientRangeType.MONTHLY_MAX_VALUE_OF_OUTGOING_PAYMENT}
            baseCurrency={baseCurrency}
            clientType={type}
          />
        </VolumesItem>
      </>
    );
  }

  return (
    <div className={cn.volumesBottom}>
      <div className={cn.volumesTableWrapper} ref={scrollRef}>
        {content}
      </div>
      <Outlet />
    </div>
  );
};

export default VolumesList;
